/**
 * Cart auto-drawer
 *
 * Makes sure the cart drawer slides open after anything adds to the cart — the
 * theme's own add-to-cart button, or a free-gift / BOGO app adding its gift.
 *
 * Why this is needed: the Tek Labs "Gifts & Upsell" app (Salepify) adds the free
 * gift right after the customer's own add-to-cart, then asks the theme to refresh
 * the cart. It has built-in refresh routines for ~60 themes, but not Horizon, and
 * for unknown themes it falls back to `window.location.reload()`. So the drawer
 * would start opening, the gift would be added, and the whole page would reload
 * with the drawer shut.
 *
 * Two parts:
 *
 * 1. `window.tlGetRefreshCartFunc` — the app's documented hook. It only installs
 *    its own version if one isn't defined yet, so defining it here (before the
 *    app's scripts run) makes it re-render the drawer and open it instead of
 *    reloading the page.
 *
 * 2. A generic safety net for any other app: watch every successful POST to
 *    Shopify's /cart/add endpoint (fetch and XMLHttpRequest). If no theme
 *    `cart:update` event follows shortly after, fetch the cart and dispatch the
 *    theme's own CartAddEvent, which the cart drawer (auto-open), cart icon
 *    badge and drawer line items already listen for.
 *
 * This is a classic (non-module, non-deferred) script on purpose: it has to run
 * before app scripts in <head> save their own copy of `window.fetch`, and before
 * the app defines its refresh hook. It only defines functions at load time, so
 * blocking is negligible. Theme modules are loaded lazily via dynamic import().
 */
(function () {
  // Matches /cart/add.js and /cart/add, with or without a market/locale prefix
  // (e.g. /en-us/cart/add.js) and with any query string (e.g. ?app=salepify).
  var CART_ADD_PATTERN = /\/cart\/add(\.js)?(\?.*)?$/;

  // How long to wait after a successful add-to-cart call before assuming no theme
  // event is coming and stepping in. Long enough for the theme's own product-form
  // fetch-then-dispatch sequence to finish first.
  var FALLBACK_DELAY_MS = 700;

  // Timestamp of the latest cart:update event, or of a refresh this script started.
  var lastCartActivityAt = 0;

  // Sources that mean "something was just added" (as opposed to quantity changes
  // or removals inside the drawer itself, which also fire cart:update).
  var ADD_SOURCES = ['product-form-component', 'cart-auto-drawer'];

  document.addEventListener('cart:update', function (event) {
    lastCartActivityAt = Date.now();

    // Open the drawer after every successful add, even if the theme setting
    // "Add to cart auto-opens drawer" gets switched off again in the editor.
    // showDialog() is a no-op when the drawer is already open.
    var data = (event.detail && event.detail.data) || {};
    if (data.didError || ADD_SOURCES.indexOf(data.source) === -1) return;

    var drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  });

  /**
   * Fetches the current cart plus fresh HTML for every cart-items-component on the
   * page (the drawer's line items, and the cart page's, if present), then
   * dispatches CartAddEvent — mirroring what a native add-to-cart does. The
   * cart:update listener above opens the drawer on that event.
   */
  async function refreshAndOpenCartDrawer() {
    lastCartActivityAt = Date.now();

    var events = await import('@theme/events');
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    var cart = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (response) {
      return response.json();
    });

    var sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map(function (element) {
        return element instanceof HTMLElement ? element.dataset.sectionId : null;
      })
      .filter(Boolean);

    var sections = {};

    if (sectionIds.length > 0) {
      var url = new URL(window.location.pathname, window.location.origin);
      url.searchParams.set('sections', sectionIds.join(','));

      sections = await fetch(url.toString(), { headers: { Accept: 'application/json' } }).then(function (response) {
        return response.json();
      });
    }

    document.dispatchEvent(
      new events.CartAddEvent(cart, 'cart-auto-drawer', {
        source: 'cart-auto-drawer',
        itemCount: cart.item_count,
        sections: sections,
      })
    );
  }

  window.montravRefreshCartDrawer = refreshAndOpenCartDrawer;

  // 1. Tek Labs / Salepify refresh hook (see header comment).
  window.tlGetRefreshCartFunc = function (callback) {
    return async function () {
      try {
        await refreshAndOpenCartDrawer();
      } catch (error) {
        // Same as the app's own fallback: a reload at least shows the right cart.
        window.location.reload();
        return;
      }

      if (typeof callback === 'function') await callback();
    };
  };

  // 2. Generic safety net for other apps.
  function onCartAddSucceeded() {
    var requestFinishedAt = Date.now();

    setTimeout(function () {
      // The theme (or the hook above) already handled this add — do nothing.
      if (lastCartActivityAt >= requestFinishedAt) return;

      refreshAndOpenCartDrawer().catch(function () {
        // Best-effort: whatever the app already did is unaffected.
      });
    }, FALLBACK_DELAY_MS);
  }

  var nativeFetch = window.fetch;

  if (typeof nativeFetch === 'function') {
    window.fetch = async function cartAutoDrawerFetch(input, init) {
      var response = await nativeFetch.apply(this, arguments);

      try {
        var url = input instanceof Request ? input.url : String(input == null ? '' : input);
        var method = String((init && init.method) || (input instanceof Request ? input.method : 'GET')).toUpperCase();

        if (method === 'POST' && response.ok && CART_ADD_PATTERN.test(url)) onCartAddSucceeded();
      } catch (error) {
        // Instrumentation must never affect the real fetch call or its response.
      }

      return response;
    };
  }

  if (window.XMLHttpRequest) {
    var nativeOpen = XMLHttpRequest.prototype.open;

    XMLHttpRequest.prototype.open = function (method, url) {
      try {
        if (String(method).toUpperCase() === 'POST' && CART_ADD_PATTERN.test(String(url))) {
          this.addEventListener('load', function () {
            if (this.status >= 200 && this.status < 300) onCartAddSucceeded();
          });
        }
      } catch (error) {
        // Never affect the real request.
      }

      return nativeOpen.apply(this, arguments);
    };
  }
})();
